import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, matchPath } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme/theme';
import TermsPrivacy from './pages/terms-privacy/TermsPrivacy';
import PublicSchedulePage from './pages/PublicSchedule/PublicSchedulePage';
import PublicAppointmentConfirmationPage from './pages/PublicAppointmentConfirmation/PublicAppointmentConfirmationPage';
import PublicPatientRegistrationPage from './pages/PublicPatientRegistration/PublicPatientRegistrationPage';
import PublicChangelogPage from './pages/PublicChangelog/PublicChangelogPage';
import PublicChangelogUnsubscribePage from './pages/PublicChangelog/PublicChangelogUnsubscribePage';
import PublicAppointmentConfirmationDemo from './pages/PublicAppointmentConfirmation/PublicAppointmentConfirmationDemo';
import './css/Form.css';
import './colorpalette/ColorPalette.css';
import './globals.css';
import Header from './header/Header';
import Sidebar from './sidebar/Sidebar';
import AppConfig from './consts/AppConfig';
import { DataProvider } from './api/DataProvider';
import { SystemConfigProvider, useSystemConfig } from './contexts/SystemConfigContext';
import { SubscriptionProvider, useSubscription } from './contexts/SubscriptionContext';
import { WhatsAppSenderProvider } from './contexts/WhatsAppSenderContext';
import { GetSessionAPI, GetCompanySetupStatusAPI, RefreshTokenAPI, SetAuthToken } from './api/API';
import { getAccessToken } from './api/authTokens';
import Login from './pages/login/Login';
import CompanyJoin from './pages/company-join/CompanyJoin';
import Welcome from './pages/welcome/Welcome';
import Landing from './pages/landing/Landing';
import ReferralEntry from './pages/landing/ReferralEntry';
import ForgotPassword from './pages/forgot-password/ForgotPassword';
import ResetPassword from './pages/forgot-password/ResetPassword';
import PendingVerification from './pages/email-verification/PendingVerification';
import ConfirmEmail from './pages/email-verification/ConfirmEmail';
import Menu from './menu/Menu';
import Loading from './components/loading/Loading';
import NPSForm from './pages/nps/NPSForm';
import VisualLogger from './components/visual-logger/VisualLogger';
import ReportProblemForm from './pages/support/report-problem/ReportProblemForm';
import SuggestImprovementForm from './pages/support/suggest-improvement/SuggestImprovementForm';
import ProductEvaluationForm from './pages/support/product-evaluation/ProductEvaluationForm';
import ContactForm from './pages/support/contact/ContactForm';
import CookieConsent from './components/cookieConsent/CookieConsent';
import { GuidedTourProvider } from './components/guided-tour/GuidedTourContext';
import GuidedTourOverlay from './components/guided-tour/GuidedTourOverlay';
import NotFound from './pages/not-found/NotFound';
import useDemoDataRemovedKey from './components/demo-data/useDemoDataRemovedKey';

// Lista de rotas que são sempre permitidas, mesmo sem assinatura ativa
const ALLOWED_ROUTES = [
  '/profile', // Meu perfil
  '/company/preferences', // Empresa - Preferências
  '/logout', // Deslogar
  '/system/import', // Sistema - Importar
  '/system/backup', // Sistema - Backup e dados
  '/system/novidades', // Sistema - Novidades
  '/system/about', // Sobre o sistema
  '/subscription/details', // Gerenciar assinatura
  '/subscription/plans', // Planos de assinatura
  '/subscription/history', // Histórico de assinatura
  '/indique', // Indique e ganhe
  '/subscription/stripe/success', // Sucesso no pagamento
  '/subscription/stripe/canceled', // Cancelamento de pagamento
  '/support/report-problem', // Suporte - Reportar problema
  '/support/suggest-improvement', // Suporte - Sugerir melhoria
  '/support/product-evaluation', // Suporte - Avaliação do produto
  '/support/contact' // Suporte - Entrar em contato
];

// Rotas logadas declaradas direto em ProtectedRoutes (fora do AppConfig.Routes).
// Mantenha em sincronia com os <Route> fixos de lá.
const EXTRA_PRIVATE_PATHS = [
  '/welcome',
  '/ativar-acesso',
  '/system/export',
  '/company/resources',
  '/support/report-problem',
  '/support/suggest-improvement',
  '/support/product-evaluation',
  '/support/contact',
];

/**
 * Diz se o endereço é uma tela do app (logado). Deslogado, um endereço que não
 * é tela do app mostra "não encontrada" em vez de mandar para o login.
 */
const isKnownPrivatePath = (pathname) =>
  [...AppConfig.Routes.map(route => route.path), ...EXTRA_PRIVATE_PATHS]
    .some(path => path && matchPath(path, pathname));

/**
 * Componente de proteção de rota que verifica a assinatura
 * Redireciona para /subscription/plans se o usuário não tiver assinatura ativa
 * e a rota não for permitida
 */
const SubscriptionProtectedRoute = ({ element, hasActiveSubscription, isSubscriptionLoading, userRole, path }) => {
  // Verificar se a rota atual é uma das rotas permitidas
  const isAllowedRoute = ALLOWED_ROUTES.some(route => path === route);
  
  // Se a verificação da assinatura ainda estiver em andamento, renderizar o elemento normalmente
  // para evitar redirecionamentos prematuros
  if (isSubscriptionLoading) {
    return element;
  }
  
  // Se o usuário não tiver assinatura ativa, e a rota não for permitida, redirecionar para /subscription/plans
  if (!hasActiveSubscription && !isAllowedRoute) {
    return <Navigate to="/subscription/plans" replace />;
  }
  
  // Caso contrário, renderizar o elemento normalmente
  return element;
};

/**
 * Componente que renderiza as rotas protegidas
 * Usa o contexto de assinatura para verificar o status da assinatura
 */
const ProtectedRoutes = ({
  isLoading,
  isMobile,
  openMenuHandler,
  menuHideHandler,
  mobileMenuVisibility,
  session
}) => {
  // Obter dados do contexto de assinatura
  const {
    hasActiveSubscription: contextHasActiveSubscription,
    loading: isSubscriptionContextLoading,
    refreshSubscription
  } = useSubscription();
  // Remover os dados de exemplo remonta a tela atual, que recarrega sem eles
  const demoDataRemovedKey = useDemoDataRemovedKey();
  // Configuração inicial em foco: sem menu nem cabeçalho, só o assistente
  const { pathname } = useLocation();
  const focusMode = pathname.startsWith('/setup-wizard');
  
  // Obter o papel do usuário na empresa atual
  const userRole = session?.companies?.find(company =>
    company?.id == session?.currentCompany
  )?.role?.name;
  
  // Remover chamada desnecessária - o SubscriptionContext já gerencia o cache
  // useEffect(() => {
  //   refreshSubscription();
  // }, [refreshSubscription]);

  return (
    <div className='app-principal'>
      {isLoading ? (
        <div className="page-content">
          <Loading />
        </div>
      ) : (
        <>
          {!isMobile && !focusMode && <Sidebar
            disableNavigation={false} // Não desabilitar a navegação, o SubscriptionProtectedRoute já cuida disso
            allowedRoutes={ALLOWED_ROUTES}
          />}
          <div className="app-content" style={focusMode ? { marginLeft: 0 } : undefined}>
            {!focusMode && <Header isMobile={isMobile} OpenMenuCallback={openMenuHandler} />}
            <Routes key={demoDataRemovedKey}>
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/ativar-acesso" element={<Navigate to="/subscription/plans" replace />} />
              <Route path="/system/export" element={<Navigate to="/system/backup" replace />} />
              <Route path="/company/resources" element={<Navigate to="/company/agendas" replace />} />
              <Route path="/support/report-problem" element={<ReportProblemForm />} />
              <Route path="/support/suggest-improvement" element={<SuggestImprovementForm />} />
              <Route path="/support/product-evaluation" element={<ProductEvaluationForm />} />
              <Route path="/support/contact" element={<ContactForm />} />
              {AppConfig.Routes.filter(route => route.id != 'nps-form').map((route) => {
                // Check if route requires platform admin access (independent of company role/subscription)
                if (route.platformAdminOnly) {
                  return (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={
                        session?.user?.isPlatformAdmin ? (
                          route.element
                        ) : (
                          <Navigate to="/dashboard" replace />
                        )
                      }
                    />
                  );
                }

                // Check if route requires admin access
                if (route.adminOnly) {
                  // Only render the route if user is COMPANY_OWNER or ADMIN
                  return (
                    <Route
                      key={route.path}
                      path={route.path}
                      element={
                        userRole === 'COMPANY_OWNER' || userRole === 'ADMIN' ? (
                          <SubscriptionProtectedRoute
                            element={route.element}
                            hasActiveSubscription={contextHasActiveSubscription()}
                            isSubscriptionLoading={isSubscriptionContextLoading}
                            userRole={userRole}
                            path={route.path}
                          />
                        ) : (
                          <Navigate to="/dashboard" replace />
                        )
                      }
                    />
                  );
                }
                
                // Regular route with subscription protection
                return (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={
                      <SubscriptionProtectedRoute
                        element={route.element}
                        hasActiveSubscription={contextHasActiveSubscription()}
                        isSubscriptionLoading={isSubscriptionContextLoading}
                        userRole={userRole}
                        path={route.path}
                      />
                    }
                  />
                );
              })}
              <Route path="*" element={<NotFound homePath="/dashboard" />} />
            </Routes>
          </div>
          <GuidedTourOverlay />
          {mobileMenuVisibility && !focusMode && <Menu
            OnHide={menuHideHandler}
            disableNavigation={false} // Não desabilitar a navegação, o SubscriptionProtectedRoute já cuida disso
            allowedRoutes={ALLOWED_ROUTES}
          />}
        </>
      )}
    </div>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <SystemConfigProvider>
        <AppContent />
      </SystemConfigProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < AppConfig.MinScreenSizeX);
  const [mobileMenuVisibility, setMobileMenuVisibility] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [currentCompanyId, setCurrentCompanyId] = useState(null);
  const { config, publicConfig, isLoading: isConfigLoading } = useSystemConfig();

  // State to track if session loading is complete
  const [isSessionLoaded, setIsSessionLoaded] = useState(false);

  // Carrega a sessão ao abrir o app. O access token vive só em memória (L-21) e
  // some no reload: se este navegador estava logado (sessionData salvo), renova o
  // token pelo cookie HttpOnly e então carrega a sessão. Sem cookie válido, a camada
  // de API limpa o estado salvo e o visitante segue deslogado (as rotas privadas já
  // mandam para o login; as públicas continuam onde estão).
  useEffect(() => {
    const loadSession = async () => {
      const wasLoggedIn = !!(localStorage.getItem('sessionData') || localStorage.getItem('userData'));

      if (!wasLoggedIn) {
        setIsSessionLoaded(true);
        return;
      }

      try {
        await new Promise((resolve, reject) => {
          RefreshTokenAPI((refreshResult) => {
            if (refreshResult.error || !getAccessToken()) {
              reject(refreshResult.error || 'Sessão expirada');
              return;
            }

            GetSessionAPI((result) => {
              if (!result.error) {
                setSession(result.session);
                setCurrentCompanyId(result.session.currentCompany);
                resolve();
              } else {
                reject(result.error);
              }
            });
          });
        });
      } catch (error) {
        window.visualLogger?.log('Sessão não restaurada: ' + error);
      } finally {
        setIsSessionLoaded(true);
      }
    };

    loadSession();
  }, []);

  // Check if system config is loaded
  // Considera carregado se publicConfig existe OU se o loading terminou (com ou sem erro)
  // Isso evita loading infinito em IABs (Instagram, Facebook) onde a API pode falhar
  const isSystemConfigLoaded = !!publicConfig || !isConfigLoading;

  const handleResize = () => {
    let isMobile = window.innerWidth < AppConfig.MinScreenSizeX;
    setIsMobile(isMobile);
    if (!isMobile && mobileMenuVisibility) {
      setMobileMenuVisibility(false);
    }
  };

  useEffect(() => {
    handleResize(); 
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (session) {
      // Store session data in localStorage for use by other components
      localStorage.setItem('sessionData', JSON.stringify(session));

      // intendedRoute vale só para o próximo login: o <Navigate> da rota /login já
      // leu o valor neste render (efeitos dos filhos rodam antes), então descarta aqui.
      sessionStorage.removeItem('intendedRoute');
      
      if (session.currentCompany) {
        setCurrentCompanyId(session.currentCompany);
      }
    }
  }, [session]);

  /**
   * Verifica se o dono da empresa precisa concluir o onboarding inicial.
   *
   * Nota: a checagem de assinatura ativa é feita por SubscriptionProtectedRoute,
   * que usa SubscriptionContext (fonte única do estado de assinatura) e redireciona
   * por rota. Não duplicar essa checagem aqui — um segundo mecanismo baseado em
   * GetCompanySubscriptionAPI chegou a tratar qualquer erro de rede/timeout como
   * "sem plano" e forçar window.location.replace('/subscription/plans'), descartando
   * formulários em andamento mesmo para a empresa 0 (pagante).
   */
  useEffect(() => {
    if (session && session.currentCompany) {
      const currentPath = window.location.pathname;

      // Evitar redirecionamento em loop na própria configuração inicial ou em subscription/plans
      if (currentPath === '/setup-wizard' || currentPath === '/subscription/plans') {
        return;
      }

      // Obter o papel do usuário na empresa atual
      const userRole = session?.companies?.find(company =>
        company?.id == session?.currentCompany
      )?.role?.name;

      // Verificações específicas para COMPANY_OWNER
      if (userRole === 'COMPANY_OWNER') {
        GetCompanySetupStatusAPI((statusData) => {
          if (statusData && !statusData.error) {
            if (statusData.status === 'INITIAL' || statusData.status === 'IN_PROGRESS') {
              window.location.replace('/setup-wizard');
            }
          } else {
            // Permissivo: erro de rede/timeout não deve bloquear o usuário
          }
        }, session.currentCompany);
      }
    }
  }, [session]);

  /**
   * Verifica se o usuário está autenticado e tem uma assinatura ativa
   * @returns {boolean} - True se o usuário estiver autenticado e tiver assinatura ativa
   */
  function checkAuthentication() {
    const hasToken = getAccessToken() != null;
    const isAuthenticated = session != null && hasToken;
    // Dispatch event for testing
    window.dispatchEvent(new CustomEvent('authStateChanged', {
      detail: { isAuthenticated, hasToken, hasSession: !!session }
    }));
    
    // Se o usuário não estiver autenticado, retorna false
    if (!isAuthenticated) {
      return false;
    }
    
    // Se o caminho atual for /subscription/plans, retorna true para evitar redirecionamento em loop
    if (window.location.pathname === '/subscription/plans') {
      return true;
    }
    
    // Se o usuário estiver autenticado, retorna true
    // O redirecionamento para /subscription/plans será feito pelo SubscriptionProtectedRoute
    // após a verificação completa da assinatura
    return true;
  }

  /**
   * Verifica se o sistema está pronto para prosseguir
   * @returns {boolean} - True se o sistema estiver pronto
   */
  function isSystemReady() {
    return isSystemConfigLoaded && isSessionLoaded;
  }
  
  /**
   * Verifica se o sistema está pronto para verificar a assinatura
   * @returns {boolean} - True se o sistema estiver pronto para verificar a assinatura
   */
  function isReadyForSubscriptionCheck() {
    return isSystemReady() && session && session.currentCompany;
  }

  function openMenuHandler() {
    setMobileMenuVisibility(true);
  }

  function menuHideHandler() {
    setMobileMenuVisibility(false);
  }

  function dataProviderCompleteHandler() {
    setIsLoading(false);
  }
  
  function loginCompleteHandler(loginData) {
    // Verificar se o token existe no loginData
    if (loginData && loginData.token) {
      // Guarda o access token em memória e avisa o SystemConfigContext.
      // O refresh token já ficou no cookie HttpOnly gravado pelo servidor.
      SetAuthToken(loginData.token);

      // Verificar se a sessão existe
      if (loginData.session) {
        setSession(loginData.session);
        // Armazenar a sessão no localStorage imediatamente para evitar problemas de sincronização
        localStorage.setItem('sessionData', JSON.stringify(loginData.session));
      } else {
        // Se não houver sessão, tentar obter a sessão usando o token recém-armazenado
        GetSessionAPI((sessionResult) => {
          if (!sessionResult.error) {
            setSession(sessionResult.session);
            localStorage.setItem('sessionData', JSON.stringify(sessionResult.session));
          }
        });
      }
    }
  }

  return (
    <BrowserRouter>
      <VisualLogger />
      <CookieConsent />
      <Routes>
          {/* Rotas públicas (sem autenticação) */}
          <Route path="/agendar/:companySlug" element={<PublicSchedulePage />} />
          <Route path="/agendar/:companySlug/:resourceSlug" element={<PublicSchedulePage />} />
          <Route path="/confirmar-demo" element={<PublicAppointmentConfirmationDemo />} />
          <Route path="/confirmar/:companySlug/:token" element={<PublicAppointmentConfirmationPage />} />
          <Route path="/cadastro/:token" element={<PublicPatientRegistrationPage />} />
          <Route path="/avaliacao/:token" element={<NPSForm />} />
          <Route path="/avaliacao" element={<NPSForm />} />
          {/* Cadastro antigo por código de convite: o cadastro é o formulário da landing */}
          <Route path="/company-signup" element={<Navigate to="/" replace />} />
          <Route path="/company-join" element={<CompanyJoin onSuccess={loginCompleteHandler} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/confirme-seu-email" element={<PendingVerification onSuccess={loginCompleteHandler} />} />
          <Route path="/confirmar-email" element={<ConfirmEmail onSuccess={loginCompleteHandler} />} />
          <Route path="/terms-privacy" element={<TermsPrivacy />} />
          <Route path="/novidades" element={<PublicChangelogPage />} />
          <Route path="/novidades/descadastrar" element={<PublicChangelogUnsubscribePage />} />
          {/* Link de indicação: guarda o código e abre a landing */}
          <Route path="/i/:code" element={<ReferralEntry isSessionLoaded={isSessionLoaded} isAuthenticated={checkAuthentication()} />} />
          <Route path="/login" element={
            !isSessionLoaded ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                width: '100vw'
              }}>
                <Loading color="secondary" />
              </div>
            ) : checkAuthentication() ?
              <Navigate to={sessionStorage.getItem('intendedRoute') || "/dashboard"} replace={true} />
              : <Login onSuccess={loginCompleteHandler} />
          } />
          <Route path="/" element={
            !isSystemReady() ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                width: '100vw'
              }}>
                <Loading color="secondary" />
              </div>
            ) : (
              checkAuthentication() ? <Navigate to="/dashboard" /> : <Landing />
            )
          } />

          {/* Rotas que requerem autenticação */}
          <Route
            path="*"
            element={
              !isSystemReady() ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100vh',
                  width: '100vw'
                }}>
                  <Loading color="secondary" />
                </div>
              ) : !checkAuthentication() ? (
                !isKnownPrivatePath(window.location.pathname) ? (
                  <NotFound homePath="/" standalone />
                ) : (() => {
                  // Store intended route before redirecting to login
                  sessionStorage.setItem('intendedRoute', window.location.pathname);
                  return <Navigate to="/login" />;
                })()
              ) : (
                <DataProvider session={session} OnComplete={dataProviderCompleteHandler}>
                  <WhatsAppSenderProvider>
                    <SubscriptionProvider>
                      <GuidedTourProvider>
                        <ProtectedRoutes
                          isLoading={isLoading}
                          isMobile={isMobile}
                          openMenuHandler={openMenuHandler}
                          menuHideHandler={menuHideHandler}
                          mobileMenuVisibility={mobileMenuVisibility}
                          session={session}
                        />
                      </GuidedTourProvider>
                    </SubscriptionProvider>
                  </WhatsAppSenderProvider>
                </DataProvider>
              )
            }
          />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
